import { useEffect, useState, type FormEvent, type JSX, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api.ts';

/**
 * The three pages a letter links to.
 *
 * Every one of them existed on the server long before it existed here: the API happily sent
 * /reset-password, /verify-email and /invite links, and the client answered all three with the
 * login form, silently dropping the token. A letter whose link goes nowhere is worse than one
 * never sent — the person did exactly what they were told and it did not work.
 *
 * They share a shell because they share a shape: take a token out of the address, do one thing
 * with it, and say plainly whether it worked.
 */

function Shell({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <div className="center">
      <div className="auth stack">
        <div>
          <div className="brand" style={{ fontSize: '1.4rem', marginBottom: 6 }}>
            Junior<span>Prog</span>
          </div>
          <p className="muted">{title}</p>
        </div>
        <div className="card">{children}</div>
      </div>
    </div>
  );
}

/** A missing token means a mangled link — say so instead of showing a form that cannot work. */
function NoToken(): JSX.Element {
  return (
    <>
      <p className="error">Ссылка неполная — в ней нет кода.</p>
      <p className="muted">
        Скорее всего она разорвалась при пересылке. Откройте её из письма целиком.
      </p>
      <a className="btn" href="/" style={{ display: 'block', textAlign: 'center' }}>
        На страницу входа
      </a>
    </>
  );
}

export function ResetPasswordPage(): JSX.Element {
  const [step, setStep] = useState<'ask' | 'code'>('ask');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError('');

    if (step === 'ask') {
      setBusy(true);
      // Always the same answer, address known or not: whether somebody studies here is not a
      // fact this form should hand to a stranger.
      await api.forgotPassword(email.trim()).catch(() => undefined);
      setBusy(false);
      setStep('code');
      return;
    }

    if (password !== repeat) {
      setError('Пароли не совпадают.');
      return;
    }

    setBusy(true);
    try {
      await api.resetPassword(email.trim(), code.trim(), password);
      setDone(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось сменить пароль');
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <Shell title="Новый пароль">
        <p className="notice notice--done">Пароль изменён. Теперь войдите с ним.</p>
        <a className="btn" href="/" style={{ display: 'block', textAlign: 'center' }}>
          Войти
        </a>
      </Shell>
    );
  }

  return (
    <Shell title="Новый пароль">
      <form onSubmit={(e) => void submit(e)}>
        {error ? <p className="error">{error}</p> : null}

        {step === 'ask' ? (
          <>
            <p className="muted" style={{ marginBottom: 16 }}>
              Введите почту — пришлём код с адреса info@juniorprog.kz.
            </p>
            <div className="field">
              <label htmlFor="reset-email">Почта</label>
              <input
                id="reset-email"
                className="input"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </>
        ) : (
          <>
            <p className="muted" style={{ marginBottom: 16 }}>
              Если такой адрес у нас есть, код уже отправлен на {email}.
            </p>

            <div className="field">
              <label htmlFor="reset-code">Код из письма</label>
              <input
                id="reset-code"
                className="input code-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                required
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/gu, ''))}
                placeholder="000000"
              />
            </div>

            <div className="field">
              <label htmlFor="new-password">Новый пароль</label>
              <input
                id="new-password"
                className="input"
                type="password"
                autoComplete="new-password"
                required
                minLength={10}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <span className="muted" style={{ fontSize: '0.8rem' }}>
                Не короче 10 символов.
              </span>
            </div>

            <div className="field">
              <label htmlFor="repeat-password">Ещё раз</label>
              <input
                id="repeat-password"
                className="input"
                type="password"
                autoComplete="new-password"
                required
                value={repeat}
                onChange={(e) => setRepeat(e.target.value)}
              />
            </div>
          </>
        )}

        <button className="btn" type="submit" disabled={busy} style={{ width: '100%' }}>
          {busy ? '…' : step === 'ask' ? 'Прислать код' : 'Сменить пароль'}
        </button>
      </form>
    </Shell>
  );
}

export function VerifyEmailPage(): JSX.Element {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<'working' | 'done' | 'failed'>('working');
  const [error, setError] = useState('');

  // Nothing to decide here, so nothing to click: the page does the one thing it exists for.
  useEffect(() => {
    if (!token) return;

    api
      .verifyEmail(token)
      .then(() => setState('done'))
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : 'Не удалось подтвердить почту');
        setState('failed');
      });
  }, [token]);

  if (!token) {
    return (
      <Shell title="Подтверждение почты">
        <NoToken />
      </Shell>
    );
  }

  return (
    <Shell title="Подтверждение почты">
      {state === 'working' ? <p className="muted">Проверяем…</p> : null}

      {state === 'done' ? (
        <>
          <p className="notice notice--done">Почта подтверждена.</p>
          <a className="btn" href="/" style={{ display: 'block', textAlign: 'center' }}>
            Войти
          </a>
        </>
      ) : null}

      {state === 'failed' ? (
        <>
          <p className="error">{error}</p>
          <a className="btn btn--ghost" href="/" style={{ display: 'block', textAlign: 'center' }}>
            На страницу входа
          </a>
        </>
      ) : null}
    </Shell>
  );
}

export function InvitePage(): JSX.Element {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError('');

    try {
      await api.acceptInvitation({ token, full_name: fullName.trim(), password });
      setDone(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось принять приглашение');
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <Shell title="Приглашение">
        <NoToken />
      </Shell>
    );
  }

  return (
    <Shell title="Приглашение в школу">
      {done ? (
        <>
          {/*
           * Accepting creates the account and stops there — it does not sign anybody in. Saying
           * so and pointing at the login form is honest; pretending to be logged in would end in
           * a blank screen.
           */}
          <p className="notice notice--done">
            Готово. Войдите с почтой, на которую пришло приглашение, и паролем, который вы только
            что придумали.
          </p>
          <a className="btn" href="/" style={{ display: 'block', textAlign: 'center' }}>
            Войти
          </a>
        </>
      ) : (
        <form onSubmit={(e) => void submit(e)}>
          {error ? <p className="error">{error}</p> : null}

          <p className="muted" style={{ marginBottom: 16 }}>
            Придумайте пароль — с ним вы будете входить.
          </p>

          <div className="field">
            <label htmlFor="invite-name">Имя и фамилия</label>
            <input
              id="invite-name"
              className="input"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="invite-password">Пароль</label>
            <input
              id="invite-password"
              className="input"
              type="password"
              autoComplete="new-password"
              required
              minLength={10}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <span className="muted" style={{ fontSize: '0.8rem' }}>
              Не короче 10 символов.
            </span>
          </div>

          <button className="btn" type="submit" disabled={busy} style={{ width: '100%' }}>
            {busy ? 'Принимаем…' : 'Принять приглашение'}
          </button>
        </form>
      )}
    </Shell>
  );
}
